import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PartnerRequest, listPartnerRequests } from '../../api/marketplace';
import {
  BID_STATUS_LABELS,
  URGENCY_LABELS,
  formatCountdown,
  formatCountries,
  serviceLabel,
  useNow,
} from './marketplaceFormat';
import './Marketplace.css';

const PartnerBoardPage: React.FC = () => {
  const { currentUser } = useAuth();
  const now = useNow();
  const [requests, setRequests] = useState<PartnerRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      setRequests(await listPartnerRequests(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load open requests.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="marketplace-page">
      <div className="task-view-header">
        <h2>Marketplace board</h2>
        <button type="button" className="download-button" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>
      <p className="marketplace-hint">
        Open requests that match your firm's services. Customer details are shared only with the
        firm the customer chooses.
      </p>
      {loading && requests.length === 0 ? (
        <p>Loading requests...</p>
      ) : error ? (
        <div className="status-error-banner" role="alert">{error}</div>
      ) : requests.length === 0 ? (
        <p className="marketplace-empty">No open requests right now.</p>
      ) : (
        <ul className="marketplace-cards">
          {requests.map(item => (
            <li key={item.id} className="marketplace-card">
              <Link to={`/marketplace/requests/${item.id}`} className="marketplace-card-link">
                <div className="marketplace-card-head">
                  <strong>{serviceLabel(item.serviceType)}</strong>
                  <span className="marketplace-countdown">{formatCountdown(item.bidDeadline, now)}</span>
                </div>
                <div className="marketplace-meta">
                  <span>{formatCountries(item.countries)}</span>
                  <span>Language: {(item.language || 'n/a').toUpperCase()}</span>
                  <span className={`marketplace-urgency marketplace-urgency--${item.urgency}`}>
                    {URGENCY_LABELS[item.urgency] || item.urgency} urgency
                  </span>
                  {item.myBid && (
                    <span className={`marketplace-badge marketplace-badge--${item.myBid.status}`}>
                      My bid: {BID_STATUS_LABELS[item.myBid.status] || item.myBid.status}
                    </span>
                  )}
                </div>
                <p className="marketplace-summary">{item.anonymisedSummary}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PartnerBoardPage;
